// pages/AdminReports.js - Admin dashboard for managing promotion reports
import React, { useState, useEffect } from 'react';
import { Flag, Eye, CheckCircle, XCircle, Clock, MessageSquare, Coins, Trash2 } from 'lucide-react';
import reportService from '../services/ReportService';
import transactionService from '../services/TransactionService';

function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch reports on component mount
  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      let result;
      if (statusFilter === 'all') {
        result = await reportService.getAllReports();
      } else {
        result = await reportService.getReportsByStatus(statusFilter);
      }

      if (result.success) {
        setReports(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (reportId, newStatus) => {
    try {
      setUpdatingStatus(true);

      const result = await reportService.updateReportStatus(reportId, newStatus, adminNotes);

      if (result.success) {
        // If marking as reviewed and user hasn't been rewarded yet, give reward
        if (newStatus === 'reviewed' && selectedReport.userId && !selectedReport.rewarded) {
          const rewardResult = await transactionService.addRewardTransaction(
            selectedReport.userId,
            5000, // IDR 5000
            'report_reward',
            `Reward for reporting issue with ${selectedReport.promotionTitle}`,
            reportId
          );

          if (rewardResult.success) {
            // Mark report as rewarded
            await reportService.markReportAsRewarded(reportId);
            alert('Report status updated and user rewarded IDR 5,000!');
          } else {
            alert('Report status updated but failed to reward user. Please check manually.');
          }
        } else {
          alert('Report status updated successfully!');
        }

        // Refresh reports
        await fetchReports();
        // Close modal
        setSelectedReport(null);
        setAdminNotes('');
      } else {
        alert('Failed to update report status');
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Failed to update report status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      try {
        const result = await reportService.deleteReport(reportId);

        if (result.success) {
          alert('Report deleted successfully!');
          // Refresh reports
          await fetchReports();
        } else {
          alert('Failed to delete report');
        }
      } catch (error) {
        console.error('Error deleting report:', error);
        alert('Failed to delete report');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-800', icon: Eye, text: 'Reviewed' },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Resolved' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  const filteredReports = reports.filter(report => {
    if (statusFilter === 'all') return true;
    return report.status === statusFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Flag className="mr-3 text-red-600" size={28} />
                Wrong Info Reports
              </h1>
              <p className="mt-1 text-gray-600">
                Manage user reports about incorrect promotion information
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                Total Reports: <span className="font-bold text-gray-900">{reports.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
            <div className="flex space-x-2">
              {['all', 'pending', 'reviewed', 'resolved'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  {status !== 'all' && (
                    <span className="ml-1">
                      ({reports.filter(r => r.status === status).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading reports...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Failed to Load Reports</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchReports}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Reports Table */}
      {!loading && !error && (
        <div className="container mx-auto px-4 pb-8">
          {filteredReports.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Flag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Reports Found</h3>
              <p className="text-gray-500">
                {statusFilter === 'all' 
                  ? 'No reports have been submitted yet.'
                  : `No ${statusFilter} reports found.`
                }
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                        Report Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Promotion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status & Date
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReports.map((report) => (
                      <tr
                        key={report.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedReport(report)}
                      >
                        <td className="px-6 py-4 w-2/5">
                          <div className="text-sm text-gray-900">
                            <p className="font-medium" title={report.description}>
                              {report.description}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{report.promotionTitle}</div>
                            <div className="text-gray-500">{report.brandName}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            {getStatusBadge(report.status)}
                            <div className="text-xs text-gray-500">
                              {formatDate(report.createdAt)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReport(report.id);
                            }}
                            className="inline-flex items-center px-3 py-1 border border-red-300 rounded-full text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedReport(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Flag className="h-6 w-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Report Details</h3>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Report Info */}
              <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 rounded-lg p-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Status</div>
                  <div className="mt-1">{getStatusBadge(selectedReport.status)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Submitted</div>
                  <div className="text-sm font-medium text-gray-900">{formatDate(selectedReport.createdAt)}</div>
                </div>
              </div>

              {/* Promotion Info */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Reported Promotion:</h4>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-900">{selectedReport.promotionTitle}</div>
                  <div className="text-sm text-gray-600">Brand: {selectedReport.brandName}</div>
                  <div className="text-xs text-gray-500 mt-1">ID: {selectedReport.promotionId}</div>
                </div>
              </div>

              {/* Issue Description */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Issue Description:</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedReport.description}</p>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (optional):
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows="3"
                  placeholder="Add notes about this report..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  onClick={() => handleStatusUpdate(selectedReport.id, 'reviewed')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={updatingStatus}
                >
                  <Eye className="w-4 h-4 mr-2 inline" />
                  Mark as Reviewed
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedReport.id, 'resolved')}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={updatingStatus}
                >
                  <CheckCircle className="w-4 h-4 mr-2 inline" />
                  Mark as Resolved
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReports;